import { useState } from 'react';
import { Clock, Film, ImageOff } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { AUDIO_BITRATE_KBPS, QUALITY_OPTIONS } from '@/constants/limits';
import {
  estimateAudioSizeBytes,
  estimateVideoSizeBytes,
  formatBytes,
  formatDuration,
} from '@/utils/format';
import { cn } from '@/utils/cn';
import type { MediaMetadata, OutputType, QualityPreset } from '@/types';

export interface MediaPreviewProps {
  metadata: MediaMetadata;
  outputType: OutputType;
  quality: QualityPreset;
  intervalSec: number;
}

/** Live preview of the detected media with per-output estimates. */
export function MediaPreview({ metadata, outputType, quality, intervalSec }: MediaPreviewProps) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const duration = metadata.durationSec;

  const videoRows = QUALITY_OPTIONS.filter((option) => option.value !== 'best').map((option) => ({
    ...option,
    size: estimateVideoSizeBytes(duration, option.height),
  }));

  const snapshotFrames =
    duration != null ? Math.min(Math.floor(duration / Math.max(intervalSec, 1)) + 1, 150) : null;

  return (
    <div className="glass animate-fade-in p-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative h-24 w-full shrink-0 overflow-hidden rounded-xl bg-white/5 sm:w-40">
          {metadata.thumbnailUrl && !thumbnailFailed ? (
            <img
              src={metadata.thumbnailUrl}
              alt=""
              referrerPolicy="no-referrer"
              onError={() => setThumbnailFailed(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-zinc-600">
              <ImageOff className="h-6 w-6" aria-hidden />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="line-clamp-1 text-sm font-semibold text-zinc-100">{metadata.title}</p>
          <p className="mt-0.5 text-xs text-zinc-500">{metadata.author}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{metadata.provider}</Badge>
            {duration != null && (
              <Badge variant="info">
                <Clock className="h-3 w-3" aria-hidden />
                {formatDuration(duration)}
              </Badge>
            )}
          </div>

          <div className="mt-3">
            {outputType === 'video' && (
              <div className="flex flex-wrap gap-2">
                {videoRows.map((row) => (
                  <span
                    key={row.value}
                    className={cn(
                      'rounded-xl border px-2.5 py-1 text-[11px] font-medium',
                      quality === row.value || (quality === 'best' && row.value === '1080p')
                        ? 'border-primary-500/40 bg-primary-500/15 text-primary-300'
                        : 'border-white/10 text-zinc-500'
                    )}
                  >
                    {row.value} · ~{formatBytes(row.size)}
                  </span>
                ))}
              </div>
            )}
            {outputType === 'audio' && (
              <p className="text-xs text-zinc-400">
                MP3 · {AUDIO_BITRATE_KBPS} kbps · ~
                {formatBytes(estimateAudioSizeBytes(duration, AUDIO_BITRATE_KBPS))}
              </p>
            )}
            {outputType === 'snapshots' && (
              <p className="flex items-center gap-1.5 text-xs text-zinc-400">
                <Film className="h-3.5 w-3.5" aria-hidden />
                Every {intervalSec}s{snapshotFrames != null && <> · ~{snapshotFrames} frames</>} · ZIP archive
              </p>
            )}
            <p className="mt-1.5 text-[11px] text-zinc-600">
              Estimates only — exact size is shown when processing finishes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
